import clsx from "clsx";
import * as React from "react";
import { ButtonKind, ButtonType } from "@spscommerce/ds-shared";
import {
    SpsFormComponentWrapper,
    FauxChangeEvent,
    SpsButton,
    usePatchReducer,
} from "@spscommerce/ds-react";
import { useSpsLanguage } from "@spscommerce/i18n";

export function useFormControlId(id: string, formControl?: any) {
    const ctrlId = React.useRef(id + "_ctrl");
    React.useEffect(() => {
        if (id) {
            ctrlId.current = `${id}_ctrl`;
            if (formControl) {
                formControl.id = ctrlId.current;
                formControl.update();
            }
        }
        // eslint-disable-next-line
    }, [id]);

    return ctrlId.current;
}

export function SpsPasswordInput({
    className,
    disabled = false,
    formControl,
    formMeta,
    icon,
    id,
    name,
    onChange,
    placeholder,
    ref,
    title,
    value = "",
    ...rest
}: any) {
    const { t } = useSpsLanguage();
    const [state, patchState] = usePatchReducer<{
        inputType: string;
    }>({
        inputType: "password",
    });
    const meta = formMeta || formControl;

    const textInput: any = React.useRef<HTMLInputElement>();

    function setAndPropagateValue(newValue: string, event?: React.ChangeEvent<HTMLInputElement>) {
        if (meta) {
            meta.setValue(newValue);
            meta.markAsDirty();
        }
        if (onChange) {
            event = event || new FauxChangeEvent(textInput.current);
            onChange(event);
        }
    }

    function clearAndFocus() {
        if (textInput.current) {
            textInput.current.value = "";
            setAndPropagateValue("");
            textInput.current.focus();
        }
    }

    function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
        setAndPropagateValue(event.target.value, event);
    }

    function handleToggleClick() {
        patchState({
            inputType: state.inputType === "password" ? "text" : "password",
        });
    }

    const classes = clsx("sps-text-input", className);

    return (
        <SpsFormComponentWrapper
            id={id}
            className={classes}
            formControl={formControl}
            formMeta={formMeta}
            inputRef={textInput}
            ref={ref}
        >
            <SpsButton
                className="mr-1 mb-1 sps-password-toggle"
                kind={ButtonKind.LINK}
                type={ButtonType.BUTTON}
                onClick={() => {
                    handleToggleClick();
                }}
            >
                {state.inputType === "password"
                    ? t("cpauth.showPassword", { defaultValue: "Show Password" })
                    : t("cpauth.hidePassword", { defaultValue: "Hide Password" })}
            </SpsButton>
            <div className={clsx("sps-form-control", disabled && "disabled")}>
                {icon && (
                    <i className={clsx("sps-text-input__icon", "sps-icon", `sps-icon-${icon}`)}></i>
                )}
                <input
                    data-testid="password-input"
                    type={state.inputType}
                    ref={textInput}
                    className="sps-text-input__input"
                    id={useFormControlId(id, meta)}
                    name={name}
                    placeholder={placeholder}
                    onChange={handleChange}
                    title={title}
                    value={value}
                    disabled={disabled}
                    {...rest}
                />
                {value && !disabled && (
                    <i
                        className="sps-icon sps-icon-x-circle sps-form-control__clear-btn"
                        onClick={clearAndFocus}
                    ></i>
                )}
            </div>
        </SpsFormComponentWrapper>
    );
}
